import backpack from '~/assets/icons/backpack.svg';
import car from '~/assets/icons/car.svg';
import engine from '~/assets/icons/engine.svg';
import lock from '~/assets/icons/lock.svg';
import message from '~/assets/icons/message.svg';
import mic from '~/assets/icons/mic.svg';
import phone from '~/assets/icons/phone.svg';
import question from '~/assets/icons/question_3.svg';
import radial from '~/assets/icons/radial.svg';
import repair from '~/assets/icons/repair.svg';
import report from '~/assets/icons/report_beta.svg';
import seatbelt from '~/assets/icons/seatbelt.svg';
import tablet from '~/assets/icons/tablet.svg';
import { readHudZoomFactor, useZoomFactor } from '~/hooks';
import { rage, useObservable } from '~/rage';

import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { HelpButton } from '../HelpButton';
import { playerKeysHelp, vehicleKeysHelp } from './HelpButtons.model';
import {
	StyledContainer,
	StyledOuter,
	StyledSlide,
} from './HelpButtons.styled';

const icons: { [name: string]: string } = {
	car: car,
	chat: message,
	phone: phone,
	inventory: backpack,
	mic: mic,
	tablet: tablet,
	radial: radial,
	help: question,
	lock: lock,
	seatbelt: seatbelt,
	engine: engine,
	repair: repair,
	report: report,
};

const QUESTS_TOP = 118;

interface IHelpButtonsProps {
	visible: boolean;
}

const HelpButtons: React.FC<IHelpButtonsProps> = ({ visible }) => {
	const questHudTask = useObservable(rage.quests.getHudTask());
	const dailyHudTask = useObservable(rage.dailyTasks.getHudTask());
	const isVehicle = useObservable(rage.vehicles.isPlayerVehicleDriver());
	const zoomFactor = useZoomFactor();
	const [topOffset, setTopOffset] = useState(QUESTS_TOP);
	const [animateTop, setAnimateTop] = useState(false);

	const buttons = useMemo(() => {
		return isVehicle ? vehicleKeysHelp : playerKeysHelp;
	}, [isVehicle]);

	const calculateTopOffset = useCallback(() => {
		if (typeof window === 'undefined') return;

		const questsElement = document.querySelector(
			'[data-hud-quests="true"]',
		) as HTMLElement | null;

		const safeZoomFactor = readHudZoomFactor();
		const questsBottom = questsElement
			? questsElement.getBoundingClientRect().bottom / safeZoomFactor
			: QUESTS_TOP;
		const minTop = QUESTS_TOP;

		setTopOffset(Math.max(minTop, questsBottom));
	}, []);

	useLayoutEffect(() => {
		calculateTopOffset();

		if (typeof window === 'undefined') return;

		const questsElement = document.querySelector(
			'[data-hud-quests="true"]',
		) as HTMLElement | null;
		const observers: ResizeObserver[] = [];

		if (questsElement) {
			const questsObserver = new ResizeObserver(calculateTopOffset);
			questsObserver.observe(questsElement);
			observers.push(questsObserver);
		}

		window.addEventListener('resize', calculateTopOffset);

		return () => {
			observers.forEach((observer) => observer.disconnect());
			window.removeEventListener('resize', calculateTopOffset);
		};
	}, [calculateTopOffset, isVehicle, zoomFactor, questHudTask, dailyHudTask]);

	useEffect(() => {
		let raf2 = 0;
		let cancelled = false;
		const raf1 = requestAnimationFrame(() => {
			if (cancelled) return;
			calculateTopOffset();
			raf2 = requestAnimationFrame(() => {
				if (!cancelled) setAnimateTop(true);
			});
		});
		return () => {
			cancelled = true;
			cancelAnimationFrame(raf1);
			cancelAnimationFrame(raf2);
		};
	}, [calculateTopOffset]);

	return (
		<StyledOuter $top={topOffset} $animateTop={animateTop}>
			<CSSTransition
				in={visible}
				classNames="keys"
				timeout={600}
				appear
				unmountOnExit={false}
			>
				<StyledSlide>
					<StyledContainer>
						{buttons.map((button, index) => (
							<HelpButton
								button={button.key}
								isHelp={button.icon === 'help'}
								isReport={button.icon === 'report'}
								icon={icons[button.icon]}
								key={index}
							/>
						))}
					</StyledContainer>
				</StyledSlide>
			</CSSTransition>
		</StyledOuter>
	);
};

export { HelpButtons };
